import React, { useId } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { TextInput } from '../../../core-components/theme/components/Fields';
import type { IdentifierKind } from '../types/domain.types';

import { CountryPrefix, joinIds } from './CountryPrefix';
import { identifierLabel, identifierPlaceholder } from './identifierText';
import { groupPhoneDigits } from './phoneGrouping';

const PHONE_INPUT = { inputMode: 'tel', autoComplete: 'username', enterKeyHint: 'send' } as const;
const OTHER_INPUT = { inputMode: 'email', autoComplete: 'username', enterKeyHint: 'send', autoCapitalize: 'none', spellCheck: false } as const;

export interface IdentifierFieldProps {
  readonly inputRef?: RefObject<HTMLInputElement>;
  readonly value: string;
  readonly kind: IdentifierKind | null;
  readonly helperText: string | null;
  readonly error: boolean;
  readonly describedById?: string;
  readonly readOnly?: boolean;
  readonly autoFocus?: boolean;
  readonly onChange: (value: string) => void;
  readonly onEnter?: () => void;
}

export const IdentifierField: React.FC<IdentifierFieldProps> = ({
  inputRef,
  value,
  kind,
  helperText,
  error,
  describedById,
  readOnly = false,
  autoFocus = false,
  onChange,
  onEnter,
}) => {
  const { t } = useTranslation();
  const prefixId = useId();
  const phone = kind === 'PHONE';
  return (
    <TextInput
      inputRef={inputRef}
      size="medium"
      type="text"
      label={identifierLabel(t)}
      placeholder={identifierPlaceholder(t)}
      value={phone ? groupPhoneDigits(value) : value}
      onValueChange={onChange}
      startAdornment={phone ? <CountryPrefix id={prefixId} /> : undefined}
      helperText={helperText ?? undefined}
      error={error}
      describedById={joinIds(describedById, phone ? prefixId : undefined)}
      readOnly={readOnly}
      inputProps={phone ? PHONE_INPUT : OTHER_INPUT}
      autoFocus={autoFocus}
      onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter' || onEnter === undefined) return;
        event.preventDefault();
        onEnter();
      }}
    />
  );
};
